import React from 'react';
import { FaGithub, FaLinkedin, FaEnvelope, FaPhone } from 'react-icons/fa';

const Hero = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --accent:  #00f5d4;
          --accent2: #7b61ff;
          --accent3: #ff4ecd;
          --bg:      #07090f;
          --text:    #e8eaf6;
          --muted:   rgba(232,234,246,0.45);
          --border:  rgba(0,245,212,0.13);
        }

        /* ── SECTION ── */
        .hr-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          padding: 100px 2rem 4rem;
          font-family: 'Syne', sans-serif;
          overflow: hidden;
          background: var(--bg);
        }

        /* background layers */
        .hr-section::before {
          content: '';
          position: absolute; inset: 0; z-index: 0;
          background:
            radial-gradient(ellipse 55% 55% at 15% 40%, rgba(123,97,255,0.12) 0%, transparent 70%),
            radial-gradient(ellipse 45% 45% at 85% 60%, rgba(0,245,212,0.08) 0%, transparent 70%);
          pointer-events: none;
        }
        .hr-section::after {
          content: '';
          position: absolute; inset: 0; z-index: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent, transparent 2px,
            rgba(0,245,212,0.016) 2px, rgba(0,245,212,0.016) 4px
          );
          pointer-events: none;
        }

        /* dot grid */
        .hr-grid {
          position: absolute; inset: 0; z-index: 0;
          background-image: radial-gradient(circle, rgba(0,245,212,0.07) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 80%);
          pointer-events: none;
        }

        /* floating orbs */
        .hr-orb {
          position: absolute; border-radius: 50%;
          pointer-events: none; z-index: 0;
          filter: blur(60px);
        }
        .hr-orb-1 {
          width: 320px; height: 320px;
          top: 10%; left: -8%;
          background: rgba(123,97,255,0.12);
          animation: hrFloatOrb 8s ease-in-out infinite;
        }
        .hr-orb-2 {
          width: 260px; height: 260px;
          bottom: 8%; right: 3%;
          background: rgba(0,245,212,0.08);
          animation: hrFloatOrb 10s ease-in-out infinite reverse;
        }

        /* ── INNER GRID ── */
        .hr-inner {
          position: relative; z-index: 1;
          max-width: 1160px; margin: 0 auto; width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        /* ── LEFT ── */
        .hr-left { display: flex; flex-direction: column; }

        .hr-tag {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem; letter-spacing: 0.2em;
          text-transform: uppercase; color: var(--accent);
          margin-bottom: 1.5rem;
          opacity: 0;
          animation: hrFadeUp 0.6s ease 0.1s forwards;
        }
        .hr-tag::before {
          content: '';
          display: inline-block; width: 24px; height: 1.5px;
          background: var(--accent);
          box-shadow: 0 0 8px var(--accent);
        }

        .hr-title {
          font-size: clamp(2.4rem, 5.5vw, 4rem);
          font-weight: 800; line-height: 1.05;
          letter-spacing: -0.03em; color: #fff;
          margin-bottom: 1rem;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.2s forwards;
        }
        .hr-title .hr-name {
          display: block;
          background: linear-gradient(90deg, var(--accent) 0%, var(--accent2) 60%, var(--accent3) 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          background-size: 200% auto;
          animation: hrGradShift 4s linear infinite;
        }

        .hr-role {
          font-family: 'Space Mono', monospace;
          font-size: clamp(0.72rem, 1.4vw, 0.88rem);
          color: var(--accent2); letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
          display: flex; align-items: center; gap: 10px;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.3s forwards;
        }
        .hr-role::after {
          content: '';
          flex: 1; height: 1px;
          background: linear-gradient(90deg, rgba(123,97,255,0.5), transparent);
        }

        .hr-desc {
          font-size: clamp(0.9rem, 1.4vw, 1rem);
          color: var(--muted); line-height: 1.78;
          max-width: 480px; margin-bottom: 2rem;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.4s forwards;
        }
        .hr-desc strong { color: var(--text); font-weight: 600; }

        /* stats */
        .hr-stats {
          display: flex; gap: 2rem;
          margin-bottom: 2.5rem;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.45s forwards;
        }
        .hr-stat { display: flex; flex-direction: column; }
        .hr-stat-num {
          font-size: 1.6rem; font-weight: 800;
          color: var(--accent); line-height: 1;
        }
        .hr-stat-label {
          font-family: 'Space Mono', monospace;
          font-size: 0.62rem; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--muted);
          margin-top: 4px;
        }
        .hr-stat-div {
          width: 1px;
          background: var(--border);
          align-self: stretch;
        }

        /* buttons */
        .hr-btns {
          display: flex; gap: 14px; flex-wrap: wrap;
          margin-bottom: 2.5rem;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.5s forwards;
        }

        .hr-btn-hire {
          position: relative;
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px;
          background: transparent;
          border: 1.5px solid var(--accent);
          border-radius: 8px;
          color: var(--accent);
          font-family: 'Space Mono', monospace;
          font-size: 0.78rem; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase;
          text-decoration: none; cursor: pointer;
          overflow: hidden;
          transition: color 0.35s, background 0.35s, box-shadow 0.35s, transform 0.2s;
        }
        .hr-btn-hire::before {
          content: '';
          position: absolute; top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(0,245,212,0.15), transparent);
          transition: left 0.5s;
        }
        .hr-btn-hire:hover {
          color: #07090f;
          background: var(--accent);
          box-shadow: 0 0 28px rgba(0,245,212,0.4);
          transform: translateY(-2px);
        }
        .hr-btn-hire:hover::before { left: 100%; }
        .hr-btn-hire:hover .hr-dot { background: #07090f; animation: none; }

        .hr-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--accent);
          animation: hrBlink 1.2s infinite;
          flex-shrink: 0;
        }

        .hr-btn-projects {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: var(--text);
          font-family: 'Space Mono', monospace;
          font-size: 0.78rem; font-weight: 400;
          letter-spacing: 0.08em; text-transform: uppercase;
          text-decoration: none;
          transition: all 0.3s;
        }
        .hr-btn-projects:hover {
          border-color: rgba(255,255,255,0.25);
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          color: var(--text);
          text-decoration: none;
        }
        .hr-arrow { display: inline-block; transition: transform 0.3s; }
        .hr-btn-projects:hover .hr-arrow { transform: translateX(4px); }

        /* socials */
        .hr-socials {
          display: flex; align-items: center; gap: 6px;
          opacity: 0;
          animation: hrFadeUp 0.7s ease 0.6s forwards;
        }
        .hr-social-label {
          font-family: 'Space Mono', monospace;
          font-size: 0.62rem; letter-spacing: 0.15em;
          text-transform: uppercase; color: var(--muted);
          margin-right: 8px;
        }
        .hr-social {
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.08);
          color: var(--muted); text-decoration: none;
          background: rgba(255,255,255,0.03);
          transition: all 0.3s;
          font-size: 1rem;
        }
        .hr-social:hover {
          color: var(--accent);
          border-color: var(--border);
          transform: translateY(-3px);
          background: rgba(0,245,212,0.05);
          text-decoration: none;
        }

        /* ── RIGHT — PHOTO ── */
        .hr-right {
          display: flex; justify-content: center; align-items: center;
          opacity: 0;
          animation: hrFadeUp 0.8s ease 0.35s forwards;
        }

        .hr-photo-frame {
          position: relative;
          width: 340px; height: 400px;
        }
        /* corner accents */
        .hr-photo-frame::before,
        .hr-photo-frame::after {
          content: '';
          position: absolute;
          width: 40px; height: 40px;
          border-color: var(--accent);
          border-style: solid; z-index: 2;
        }
        .hr-photo-frame::before {
          top: -8px; left: -8px;
          border-width: 2px 0 0 2px; border-radius: 4px 0 0 0;
        }
        .hr-photo-frame::after {
          bottom: -8px; right: -8px;
          border-width: 0 2px 2px 0; border-radius: 0 0 4px 0;
        }

        .hr-photo-inner {
          width: 100%; height: 100%;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(123,97,255,0.15), rgba(0,245,212,0.08));
          position: relative;
        }

        /* profile image */
        .hr-profile-img {
          width: 100%; height: 100%;
          object-fit: cover; object-position: top center;
          display: block;
        }

        /* fallback placeholder (shown when no image) */
        .hr-photo-placeholder {
          width: 100%; height: 100%;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 12px;
        }
        .hr-avatar-ring {
          width: 140px; height: 140px; border-radius: 50%;
          border: 2px solid var(--accent);
          box-shadow: 0 0 24px rgba(0,245,212,0.2);
          background: linear-gradient(135deg, rgba(123,97,255,0.25), rgba(0,245,212,0.12));
          display: flex; align-items: center; justify-content: center;
          animation: hrPulseRing 3s ease-in-out infinite;
        }
        .hr-avatar-initials {
          font-size: 2.5rem; font-weight: 800;
          color: var(--accent);
          text-shadow: 0 0 20px rgba(0,245,212,0.5);
        }
        .hr-avatar-tag {
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem; letter-spacing: 0.15em;
          text-transform: uppercase; color: var(--accent);
          opacity: 0.65;
        }

        /* floating badge */
        .hr-badge-available {
          position: absolute; bottom: 24px; left: -20px;
          display: flex; align-items: center; gap: 8px;
          background: rgba(7,9,15,0.92);
          border: 1px solid var(--border);
          border-radius: 30px; padding: 8px 16px;
          font-family: 'Space Mono', monospace;
          font-size: 0.62rem; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--accent);
          backdrop-filter: blur(12px);
          animation: hrFloatBadge 3s ease-in-out infinite;
          white-space: nowrap;
        }
        .hr-badge-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: var(--accent);
          animation: hrBlink 1.2s infinite;
        }

        /* stack pills */
        .hr-stack-pills {
          position: absolute; top: 20px; right: -18px;
          display: flex; flex-direction: column; gap: 6px;
        }
        .hr-stack-pill {
          background: rgba(7,9,15,0.9);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 6px; padding: 5px 12px;
          font-family: 'Space Mono', monospace;
          font-size: 0.6rem; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--muted);
          backdrop-filter: blur(8px);
          animation: hrFloatBadge 3.5s ease-in-out infinite;
        }
        .hr-stack-pill:nth-child(2) { animation-delay: 0.5s; }
        .hr-stack-pill:nth-child(3) { animation-delay: 1s; }
        .hr-stack-pill span { color: var(--accent2); }

        /* ── ANIMATIONS ── */
        @keyframes hrFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hrBlink {
          0%, 100% { opacity: 1; box-shadow: 0 0 6px currentColor; }
          50%       { opacity: 0.3; box-shadow: none; }
        }
        @keyframes hrFloatBadge {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes hrFloatOrb {
          0%, 100% { transform: translate(0, 0); }
          50%       { transform: translate(20px, -20px); }
        }
        @keyframes hrPulseRing {
          0%, 100% { box-shadow: 0 0 24px rgba(0,245,212,0.2); }
          50%       { box-shadow: 0 0 40px rgba(0,245,212,0.4); }
        }
        @keyframes hrGradShift {
          0%   { background-position: 0% center; }
          100% { background-position: 200% center; }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .hr-inner {
            grid-template-columns: 1fr;
            text-align: center; gap: 3rem;
          }
          .hr-right { order: -1; }
          .hr-tag   { justify-content: center; }
          .hr-role  { justify-content: center; }
          .hr-role::after { display: none; }
          .hr-desc  { margin-left: auto; margin-right: auto; }
          .hr-stats { justify-content: center; }
          .hr-btns  { justify-content: center; }
          .hr-socials { justify-content: center; }
          .hr-photo-frame { width: 260px; height: 300px; }
          .hr-badge-available { left: 0; }
          .hr-stack-pills { right: -8px; }
        }
      `}</style>

      <section id="home" className="hr-section">
        {/* background layers */}
        <div className="hr-grid" />
        <div className="hr-orb hr-orb-1" />
        <div className="hr-orb hr-orb-2" />

        <div className="hr-inner">

          {/* ── LEFT ── */}
          <div className="hr-left">
            <div className="hr-tag">Available for work</div>

            <h1 className="hr-title">
              Hi, I'm
              <span className="hr-name">Subodh Paswan</span>
            </h1>

            <p className="hr-role">
              Full Stack Developer · MERN Stack Specialist
            </p>

            <p className="hr-desc">
              Self-taught developer passionate about building{' '}
              <strong>scalable web applications</strong>. Experienced in remote
              work with strong skills in <strong>React.js, Node.js</strong>,
              MongoDB, and modern web technologies.
            </p>

            {/* Stats */}
            <div className="hr-stats">
              <div className="hr-stat">
                <span className="hr-stat-num">1+</span>
                <span className="hr-stat-label">Years Exp</span>
              </div>
              <div className="hr-stat-div" />
              <div className="hr-stat">
                <span className="hr-stat-num">10+</span>
                <span className="hr-stat-label">Projects</span>
              </div>
              <div className="hr-stat-div" />
              <div className="hr-stat">
                <span className="hr-stat-num">MERN</span>
                <span className="hr-stat-label">Stack</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="hr-btns">
              <a href="#contact" className="hr-btn-hire"
                onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
                <span className="hr-dot" />
                Hire Me
              </a>
              <a href="#projects" className="hr-btn-projects"
                onClick={(e) => { e.preventDefault(); document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' }); }}>
                View Projects
                <span className="hr-arrow">→</span>
              </a>
            </div>

            {/* Socials */}
            <div className="hr-socials">
              <span className="hr-social-label">Find me</span>
              <a className="hr-social" href="https://github.com/SuboKumar2356" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <FaGithub />
              </a>
              <a className="hr-social" href="https://linkedin.com/in/yourusername" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedin />
              </a>
              <a className="hr-social" href="mailto:subo2356@gmail.com" aria-label="Email">
                <FaEnvelope />
              </a>
              <a className="hr-social" href="tel:+919851241219" aria-label="Phone">
                <FaPhone />
              </a>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className="hr-right">
            <div className="hr-photo-frame">
              <div className="hr-photo-inner">
                    <img src="/assets/images/profile.png" alt="Subodh Paswan" className="hr-profile-img" />
                </div>

              {/* Floating available badge */}
              <div className="hr-badge-available">
                <span className="hr-badge-dot" />
                Open to work
              </div>

              {/* Tech stack pills */}
              <div className="hr-stack-pills">
                <div className="hr-stack-pill"><span>⬡</span> React.js</div>
                <div className="hr-stack-pill"><span>⬡</span> Node.js</div>
                <div className="hr-stack-pill"><span>⬡</span> MongoDB</div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Hero;